import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { utils, write } from "xlsx";
import Papa from "papaparse";

import { DropDownField, TextInputField, CalendarField, CheckboxField } from "../../../helpers";
import Button from "../../../helpers/Button";
import { Menu } from 'primereact/menu';

import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { fetchSellerDebitNotes, updateSearchOptions, updateSearchParams, setSearchConfig } from "../../../services/redux/reducers/sellerDebitNote/sellerDebitNote";
import { searchSellerDebitNotesRaw } from "../../../services/api/adjustmentNotes";
import { getAddFilterOptions, getAutoSearchSuggestions, getDateRange, getDateString, getMatchedRegexPattern, normalizeLabel } from "../../../utils";

import DateFilter from "../../../common/DateFilter";


const vpCreditNonCreditConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Adjustment Notes Data', value: 'adjustmentNotesData' }
        ]
    },
    {
        srNO: 2,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text",

    },
    {
        srNO: 3,
        static: false,
        type: "SELECT",
        name: "branchId",
        label: "Branch",
    },
    {
        srNO: 4,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        options: [
            { label: 'Debit Note Date', value: 'createdAt' },
            { label: 'Booked Date', value: 'bookedDate' }
        ]
    },
    {
        srNO: 5,
        static: false,
        type: "SELECT",
        name: "bookedStatus",
        label: "Booking Status",
        options: [
            { label: 'Booked', value: true },
            { label: 'Unbooked', value: false },
        ]
    },
    {
        srNO: 6,
        static: false,
        type: "SELECT",
        name: "isCNUploaded",
        label: "CN Uploaded",
        options: [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
        ]
    },
    {
        srNO: 7,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "adjustmentNoteDocNo",
        label: "Credit Note No.",
    },
    {
        srNO: 8,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "sellerPODocNo",
        label: "Seller PO No",
    },
    {
        srNO: 9,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "sellerPINo",
        label: "PI No.",
    },
    {
        srNO: 10,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "userName",
        label: "User Name",
        priority: 2
    },
    {
        srNO: 11,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "companyName",
        label: "Company Name",
        priority: 1
    },
    {
        srNO: 12,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "bookedBy",
        label: "Booked By",
    },
    {
        srNO: 13,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "docNo",
        label: "Doc No",
    }
]

const Search = (props) => {

    const [suggestions, setSuggestions] = useState([])

    const isLoading = useSelector(state => state.sellerDebitNotes.loading)
    const usersData = useSelector(state => state.userDetails)
    const searchParams = useSelector(state => state.sellerDebitNotes.searchParams)
    const paginationParams = useSelector(state => state.sellerDebitNotes.paginationParams)
    const searchOptions = useSelector(state => state.sellerDebitNotes.searchOptions)
    const companyBranches = usersData?.PMCompanyDetails?.companyBranches

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const searchTextRef = useRef(null)

    const searchConfig = vpCreditNonCreditConfig

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(setSearchConfig(vpCreditNonCreditConfig))
        if (searchOptions.length === 0) {
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.searchBy])

    const addFilterOption = (srNO) => {
        const relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        let updatedConfig = { ...relatedConfig }

        if (updatedConfig.name === "branchId") {
            const updatedCompanyBranches = companyBranches?.map(eachCompanyBranch => ({ ...eachCompanyBranch, label: eachCompanyBranch.branchName, value: eachCompanyBranch.branchId }))
            updatedConfig = { ...updatedConfig, options: updatedCompanyBranches }
        }

        dispatch(updateSearchOptions([...searchOptions, updatedConfig]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            updatedSearchParams.range = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value

        if (eventValue && (eventValue.userId || eventValue.companyId)) {
            eventValue = eventValue.displayName
        }
        
        const updatedSearchParams = { ...searchParams, [event.target.name]: eventValue }

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        if (event.target.name === "searchText") {
            const availableOptions = [...vpCreditNonCreditConfig]
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            updatedSearchParams.searchBy = matchedPattern
            
        }
        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        getDateRange(range)
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const autoCompleteSearch = (searchType, query) => {
        const suggestions = getAutoSearchSuggestions({
            searchType,
            query,
        })

        setSuggestions(suggestions)
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const handleCheckboxSelect = (event) => {
        const updatedSearchParams = {
            ...searchParams,
            [event.target.name]: event.target.checked
        }
        dispatch(updateSearchParams(updatedSearchParams))
    }

    const downloadData = async (dataFormat) => {
        let response = []

        const updatedSearchParams = { ...searchParams }
        if(updatedSearchParams.searchBy !== "all"){
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }
        response = await searchSellerDebitNotesRaw({ ...updatedSearchParams, paginator: false, adjustmentTypeIds: [3, 9, 10] })

        const responseData = response.data.data

        const downloadData = []

        responseData.map(eachRow => {
            let docProofUploadedDate = ""
            if (eachRow["docProofUploadedDate"]) {
                docProofUploadedDate = getDateString(eachRow["docProofUploadedDate"])
            }

            let bookingLogBookedAt = ""
            if (eachRow["bookingLogBookedAt"]) {
                bookingLogBookedAt = getDateString(eachRow["bookingLogBookedAt"])
            }

            let bookedBy = ""
            if (eachRow["bookedUserFirstName"]) {
                bookedBy += eachRow["bookedUserFirstName"]
            }
            if (eachRow["bookedUserFirstName"]) {
                bookedBy += " " + eachRow["bookedUserLastName"]
            }

            let debitNoteCreatedDate = ""
            if (eachRow["createdAt"]) {
                debitNoteCreatedDate = getDateString(eachRow["createdAt"])
            }

            let sellerPOCreatedDate = ""
            if (eachRow["sellerPOCreatedDate"]) {
                sellerPOCreatedDate = getDateString(eachRow["sellerPOCreatedDate"])
            }

            let pmBranchName = companyBranches.find(eachBranch => eachBranch.branchId === eachRow.pmBranchId)

            const obj = {
                "Debit Note No": eachRow["docNo"],
                "Debit Note Created At": debitNoteCreatedDate,
                "Seller PO No": eachRow["sellerPODocNo"],
                "Seller PO Created At": sellerPOCreatedDate,
                "KAM": eachRow["userFirstName"] + " " + eachRow["userLastName"],
                "Company Name": eachRow["companyName"],
                "Credit Note No": eachRow["docProofNo"],
                "Buyer Credit Note No.": eachRow["linkedDocNo"],
                "CN Uploaded Date": docProofUploadedDate,
                "Purchase Invoice": eachRow["sellerPINumber"],
                "PI Date": eachRow["sellerPIDate"],
                "PI Upoladed Date": eachRow["sellerPIUploadedDate"] ? eachRow["sellerPIUploadedDate"] : "",
                "Booking Status": eachRow["bookedStatus"] ? "BOOKED" : "NOT BOOKED",
                "Booked By": bookedBy,
                "Booked Date": bookingLogBookedAt,
                "Amount": eachRow["totalAmountWithTax"],
                "Remarks": eachRow["remarks"] ? eachRow["remarks"] : "",
                "PM Branch": pmBranchName?.branchName
            }
            downloadData.push(obj)
        })

        if (dataFormat === "CSV") {
            let csv = Papa.unparse(downloadData, { download: true })
            let blob = new Blob([csv])
            let a = window.document.createElement("a")
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
            a.download = "DebitNoteBooking.csv"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
        if (dataFormat === "XLSX") {
            const ws = utils.json_to_sheet(downloadData)
            const wb = utils.book_new()
            utils.book_append_sheet(wb, ws, 'Sheet1')
            const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'DebitNoteBooking.xlsx'
            a.click()
            URL.revokeObjectURL(url)
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadData("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadData("CSV")
        }
    ]

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }

    const handleSearch = () => {
        let errorFound = false

        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        } 
        else if (searchParams.searchText && searchParams.searchBy !== "all"){
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })
        
        const updatedSearchParams = { ...searchParams }
        if (updatedSearchParams.searchBy !== "all") {
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        const docNoSearchLabels = ["docNo", "adjustmentNoteDocNo", "sellerPODocNo"]

        if (!Object.keys(updatedSearchParams).some(label => docNoSearchLabels.includes(label))) {
            if (!searchParams.fromDate || !searchParams.toDate) {
                dispatch(raiseToast({ severity: 'error', detail: "Please select from date and to date", life: 3000 }))
                errorFound = true
            }
        }
        
        if (!errorFound){
            dispatch(fetchSellerDebitNotes({ ...paginationParams, ...updatedSearchParams, adjustmentTypeIds: [3, 9, 10], pageNo: 0 }))
        }
    }

    return (
        <div className="p-card" style={{ border: "1px solid #e9ecef" }} onKeyDown={handleSearchKeyPress}>
            <div className="grid">
                {
                    searchOptions.map(eachOption => {

                        if (eachOption.type === "SEARCH_TEXT") {

                            let searchByOptions = [{ label: 'No Filters', value: 'all' }]
                            searchConfig.map(eachSearchBy => {
                                if (eachSearchBy.isSearchByOption) {
                                    const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                    if(!optionInUse){
                                        searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                    }
                                }
                            })

                            return (
                                <>
                                    <div className="flex flex-column col-12">
                                        <TextInputField
                                            name="searchText"
                                            labelText="Search Text"
                                            placeholder="No Filter"
                                            refName={searchTextRef}
                                            value={searchParams["searchText"]}
                                            onChange={handleSearchOnChange}
                                        />
                                    </div>
                                    {searchParams.searchText && (
                                        <div className="flex flex-column col-12">
                                            <DropDownField
                                                name="searchBy"
                                                labelText="Search By"
                                                options={searchByOptions}
                                                value={searchParams["searchBy"]}
                                                onChange={handleSearchOnChange}
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        }

                        if (eachOption.type === "DATE") {
                            return (
                                <>
                                    <DateFilter 
                                        eachOption={eachOption} 
                                        searchParams={searchParams} 
                                        dateOptions={["MTD", "PM", "L15", "L30"]}
                                        handleDateRangeChange={handleDateRangeChange} 
                                        handleSearchOnChange={handleSearchOnChange}
                                        removeFilterOption={removeFilterOption}
                                    />
                                </>
                            )
                        }
                        if (eachOption.type === "SELECT") {
                            const updatedOptions = eachOption.options.map(eachSelectOption => ({ label: eachSelectOption.label, value: eachSelectOption.value }))
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name={eachOption.name} options={updatedOptions} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "TEXT") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    {
                                        <div className="flex flex-column col-12">
                                            <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                        </div>
                                    }
                                </>
                            )
                        }

                    })
                }
                <div className="flex flex-column col-12">
                    <div className="flex flex-row justify-content-between">
                        <CheckboxField name="openIssues" onChange={handleCheckboxSelect} checked={searchParams.openIssues} label="Open Issues" />
                    </div>
                </div>
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0">
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isLoading} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} permissions={[251]}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Search;